<script lang="ts">
  import PanelTopBarLayout from "/@/lib/components/PanelTopBarLayout.svelte"
  import { twApiClient } from "/@/lib/tastyworks-rest"
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import BuyingPowerDialog from "/@lib/components/dialog/BuyingPowerDialog.svelte"
  import PendingCashDialog from "/@lib/components/dialog/PendingCashDialog.svelte"
  import LoadingSpinner from "/@lib/components/loading/LoadingSpinner.svelte"
  import ClickableTable from "/@lib/components/table/ClickableTable.svelte"
  import BalanceTable from "/@lib/components/table/SimpleTable.svelte"
  import type { ColumnDefinitions } from "/@lib/types/Table"
  import { formatCurrency } from "/@lib/utils/formats"
  import { MoreMinimal } from "@tastyworks/icons"
  import { AdminBalance, SingleResponse } from "@tastyworks/tastyworks-api"
  import { Button } from "@tastyworks/ui-library"
  import { Pane, PaneGroup, PaneResizer } from "paneforge"
  import type { ComponentEvents } from "svelte"

  const AVAILABLE_FUNDS_ROWS = (balance: AdminBalance) => {
    const buyingPowerAdjusted = balance.buyingPowerAdjustment !== 0
    const buyingPowerKey = buyingPowerAdjusted
      ? balance.buyingPowerAdjustmentEffect === "Credit"
        ? balance.buyingPowerAdjustment
        : -balance.buyingPowerAdjustment
      : 0
    return [
      {
        header: "Net Liquidating Value",
        key: balance.netLiquidatingValue,
      },
      {
        header: "Option Buying Power",
        key: balance.optionBuyingPower,
      },
      {
        header: "Stock Buying Power",
        key: balance.stockBuyingPower,
      },
      {
        header: "Buying Power Adjustment",
        key: buyingPowerKey,
      },
    ]
  }

  const BALANCE_POSITIONS_ROWS = (balance: AdminBalance) => {
    return [
      {
        header: "Long Stock Value",
        key: balance.longStockValue,
      },
      {
        header: "Long Options Value",
        key: balance.longOptionValue,
      },
      { header: "Long Futures Value", key: balance.longFuturesValue },
      {
        header: "Long Futures Options Value",
        key: balance.longFuturesOptionsValue,
      },
      {
        header: "Totals (Long Positions)",
        key: balance.totalLongPositions,
      },
      {
        header: "Short Stock Value",
        key: balance.shortStockValue,
      },
      {
        header: "Short Options Value",
        key: balance.shortOptionValue,
      },
      {
        header: "Short Futures Value",
        key: balance.shortFuturesValue,
      },
      {
        header: "Short Futures Options Value",
        key: balance.shortFuturesOptionsValue,
      },
      {
        header: "Total (Short Positions)",
        key: balance.totalShortPositions,
      },
      {
        header: "Total (All Positions)",
        key: balance.totalAllPositions,
      },
    ]
  }

  const BALANCES_ROWS = (balance: AdminBalance) => {
    return [
      { header: "Cash Balance", key: balance.cashBalance },
      {
        header: "Day Trading Buying Power",
        key: balance.dayTradingBuyingPower,
      },
      { header: "Pending Cash", key: balance.pendingCash.value },
      {
        header: "Pending Margin Interest",
        key: balance.pendingMarginInterest,
      },
    ]
  }

  type BalanceEntry = { header: string; key: number }

  const BALANCE_POSITIONS_COLUMNS: ColumnDefinitions = [
    {
      name: "Positions",
      dataFormatter: (entry: BalanceEntry) => entry.header,
    },
    {
      name: "Current Value",
      dataFormatter: (entry: BalanceEntry) => formatCurrency(entry.key),
      style: "currency",
    },
  ]

  const BALANCES_COLUMNS: ColumnDefinitions = [
    {
      name: "Balances",
      dataFormatter: (entry: BalanceEntry) => entry.header,
    },
    {
      name: "Current Value",
      dataFormatter: (entry: BalanceEntry) => formatCurrency(entry.key),
      style: "currency",
    },
  ]

  const AVAILABLE_FUNDS_COLUMNS: ColumnDefinitions = [
    {
      name: "Available Funds",
      dataFormatter: (entry: BalanceEntry) => entry.header,
    },
    {
      name: "Current Value",
      dataFormatter: (entry: BalanceEntry) => formatCurrency(entry.key),
      style: "currency",
    },
  ]

  let balancePromise: Promise<SingleResponse<AdminBalance>> | null =
    $state(null)
  let reloadBPromise: Promise<void> | null = $state(null)

  function searchBalances() {
    if ($selectedCustomerAccount.account) {
      balancePromise = twApiClient.AdminBalancesService.index(
        $selectedCustomerAccount.account?.accountNumber ?? ""
      )
    } else {
      balancePromise = null
    }
  }

  function adjustBuyingPower(
    event: ComponentEvents<BuyingPowerDialog>["adjustBuyingPower"]
  ) {
    if ($selectedCustomerAccount.account) {
      twApiClient.BuyingPowerService.adjustBuyingPower(
        $selectedCustomerAccount.account?.accountNumber ?? "",
        event.detail.buyingPower,
        event.detail.note
      ).then(() => {
        balancePromise = twApiClient.AdminBalancesService.index(
          $selectedCustomerAccount.account?.accountNumber ?? ""
        )
      })
    }
  }

  function reloadBuyingPower() {
    if ($selectedCustomerAccount.account) {
      reloadBPromise = twApiClient.BuyingPowerService.reloadBuyingPower(
        $selectedCustomerAccount.account?.accountNumber ?? ""
      )
    }
  }

  $effect(() => {
    searchBalances()
  })
</script>

<PanelTopBarLayout>
  <Button
    variant="secondary"
    onclick={() => {
      searchBalances()
    }}>Refresh</Button
  >
  <div class="flex flex-row gap-3">
    <PendingCashDialog />
    <Button
      variant="secondary"
      class="min-w-32"
      onclick={() => reloadBuyingPower()}
    >
      {#await reloadBPromise}
        <div class="relative h-10 w-10">
          <LoadingSpinner size="20px" />
        </div>
      {:then}
        Reload BP
      {/await}
    </Button>
    <BuyingPowerDialog on:adjustBuyingPower={adjustBuyingPower} />
  </div>
</PanelTopBarLayout>

<div class="flex h-0 flex-grow flex-col">
  {#snippet resizer()}
    <PaneResizer
      class="my-1 flex h-2 min-h-1 w-full items-center justify-center rounded-md bg-trade-desk-gray hover:bg-trade-desk-gray-dark"
    >
      <MoreMinimal />
    </PaneResizer>
  {/snippet}

  {#if !balancePromise}
    <PaneGroup
      direction="vertical"
      autoSaveId="tradedesk-balances"
      class="h-full"
    >
      <Pane defaultSize={33} minSize={10}>
        <BalanceTable columns={AVAILABLE_FUNDS_COLUMNS} message="" />
      </Pane>
      {@render resizer()}
      <Pane defaultSize={34} minSize={10}>
        <BalanceTable columns={BALANCE_POSITIONS_COLUMNS} message="" />
      </Pane>
      {@render resizer()}
      <Pane defaultSize={33} minSize={10}>
        <BalanceTable columns={BALANCES_COLUMNS} message="" />
      </Pane>
    </PaneGroup>
  {:else}
    {#await balancePromise}
      <LoadingSpinner />
    {:then results}
      <PaneGroup
        direction="vertical"
        autoSaveId="tradedesk-balances"
        class="h-full"
      >
        <Pane defaultSize={33} minSize={10}>
          {#if results.data}
            <ClickableTable
              class="h-full"
              items={AVAILABLE_FUNDS_ROWS(results.data)}
              columns={AVAILABLE_FUNDS_COLUMNS}
              resizable={false}
              fillWidth
            />
          {:else}
            <BalanceTable
              columns={AVAILABLE_FUNDS_COLUMNS}
              message="No content in table"
            />
          {/if}
        </Pane>
        {@render resizer()}
        <Pane defaultSize={34} minSize={10}>
          {#if results.data}
            <ClickableTable
              class="h-full"
              items={BALANCE_POSITIONS_ROWS(results.data)}
              columns={BALANCE_POSITIONS_COLUMNS}
              resizable={false}
              fillWidth
            />
          {:else}
            <BalanceTable
              columns={BALANCE_POSITIONS_COLUMNS}
              message="No content in table"
            />
          {/if}
        </Pane>
        {@render resizer()}
        <Pane defaultSize={33} minSize={10}>
          {#if results.data}
            <ClickableTable
              class="h-full"
              items={BALANCES_ROWS(results.data)}
              columns={BALANCES_COLUMNS}
              resizable={false}
              fillWidth
            />
          {:else}
            <BalanceTable
              columns={BALANCES_COLUMNS}
              message="No content in table"
            />
          {/if}
        </Pane>
      </PaneGroup>
    {:catch error}
      <div class="absolute flex h-full w-full items-center justify-center">
        {error.message}
      </div>
    {/await}
  {/if}
</div>
